import React from "react";
import { useNavigate } from "react-router-dom";
import {
  HeartPulse, Printer, ArrowLeft, Shield, Building2, UserCog, Stethoscope,
  Syringe, ClipboardList, Landmark, CheckCircle2, XCircle, Users, Bell,
  Calendar, MessageSquare, Megaphone, BedDouble, Cross, Phone, Mail, Globe,
  Paperclip, FileText, Eye, EyeOff, Lock, MapPin, Play, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const COLORS = {
  green: "#1E4631", orange: "#D96C4A", red: "#C62828",
  olive: "#7A8471", sand: "#E5D3B3", blue: "#2E5C7E",
  cream: "#FDFBF7", grey: "#5C6B61", border: "#E5EBE7",
};

export default function TrainingGuide() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white text-[#1A1D1A]" data-testid="training-guide">
      {/* Toolbar */}
      <div className="print:hidden border-b border-[#E5EBE7] px-6 py-3 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="tg-back">
          <ArrowLeft className="w-4 h-4 mr-1.5"/> Retour
        </Button>
        <div className="text-xs text-[#5C6B61]">Guide de formation · A4 · Ctrl+P → "Enregistrer au format PDF"</div>
        <Button onClick={() => window.print()} className="bg-[#1E4631] hover:bg-[#163424]" size="sm" data-testid="tg-print">
          <Printer className="w-4 h-4 mr-1.5"/> Imprimer / PDF
        </Button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white">
        {/* ========== PAGE 1 — COUVERTURE ========== */}
        <section className="p-16 min-h-[297mm] flex flex-col page-break relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#1E4631]/5"/>
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#D96C4A]/5"/>

          <div className="relative flex items-center justify-between pb-4 border-b border-[#E5EBE7]">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#1E4631] grid place-items-center">
                <HeartPulse className="w-6 h-6 text-white"/>
              </div>
              <div>
                <div className="font-[Outfit] font-bold text-lg">KWIKILA Care</div>
                <div className="text-xs text-[#5C6B61]">La santé en confiance · RDC</div>
              </div>
            </div>
            <div className="text-right text-xs text-[#5C6B61]">
              <div>Version 3.5</div>
              <div>Février 2026</div>
            </div>
          </div>

          <div className="relative flex-1 flex flex-col justify-center">
            <div className="text-xs uppercase tracking-widest text-[#D96C4A] font-semibold mb-3">Support de formation</div>
            <h1 className="font-[Outfit] font-bold text-5xl leading-[1.1] mb-6 max-w-2xl">
              Guide complet des<br/><span className="text-[#1E4631]">droits d'accès</span>
            </h1>
            <p className="text-lg text-[#5C6B61] max-w-xl leading-relaxed">
              Rôles, permissions et interdictions par service hospitalier —
              un manuel de référence pour former votre équipe à l'outil KWIKILA Care.
            </p>

            <div className="mt-12 grid grid-cols-3 gap-3 max-w-lg">
              <StatBox icon={Shield} num="6" label="Rôles utilisateurs"/>
              <StatBox icon={Users} num="16+" label="Permissions détaillées"/>
              <StatBox icon={Play} num="4" label="Scénarios pédagogiques"/>
            </div>
          </div>

          <div className="relative text-xs text-[#5C6B61] pt-6 border-t border-[#E5EBE7] grid grid-cols-3 gap-4">
            <div>
              <div className="uppercase tracking-widest text-[10px] mb-1">Public visé</div>
              <div>Formateurs · Admin hôpital · Personnel médical</div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-[10px] mb-1">Durée conseillée</div>
              <div>2 heures (théorie 1h + pratique 1h)</div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-[10px] mb-1">Contact</div>
              <div>contact@kwikilacare.com</div>
              <div>+243 824 314 459</div>
            </div>
          </div>
        </section>

        {/* ========== PAGE 2 — SOMMAIRE + VUE D'ENSEMBLE ========== */}
        <section className="p-16 min-h-[297mm] page-break">
          <PageHeader chapter="Chapitre 1" title="Sommaire & vue d'ensemble" pageNum={2}/>

          <div className="grid grid-cols-2 gap-8 mt-8">
            <div>
              <h2 className="font-[Outfit] font-bold text-2xl mb-4">Table des matières</h2>
              <ol className="space-y-2 text-sm">
                {[
                  ["Vue d'ensemble — 6 profils", "3"],
                  ["👑 Admin KWIKILA Care", "4"],
                  ["🏥 Admin de l'hôpital", "5"],
                  ["🩺 Médecin", "6"],
                  ["💉 Infirmier(e)", "7"],
                  ["📋 Secrétaire médical(e)", "8"],
                  ["🏛️ Compte Ministère", "9"],
                  ["Matrice récapitulative", "10"],
                  ["Scénarios pédagogiques", "11"],
                  ["Contact & support", "12"],
                ].map(([t, p], i) => (
                  <li key={i} className="flex items-center justify-between border-b border-dashed border-[#E5EBE7] pb-2">
                    <span className="text-[#1A1D1A]">{t}</span>
                    <span className="font-[Outfit] font-bold text-[#1E4631]">{p}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="bg-gradient-to-br from-[#1E4631]/5 to-[#D96C4A]/5 rounded-2xl p-6 border border-[#E5EBE7]">
              <h3 className="font-[Outfit] font-bold text-lg mb-4">Ce que vos apprenants sauront faire</h3>
              <ul className="space-y-3 text-sm">
                {[
                  "Identifier leur rôle et les limites de leurs droits",
                  "Créer et gérer un patient dans le respect du secret médical",
                  "Enregistrer une hospitalisation ou un décès selon les procédures",
                  "Planifier un RDV et déclencher les rappels SMS",
                  "Utiliser les conseils IA multilingues en 5 langues",
                  "Consulter les registres et générer les rapports officiels",
                ].map((t, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#1E4631] shrink-0 mt-0.5"/>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Diagramme hiérarchie */}
          <h3 className="font-[Outfit] font-bold text-xl mt-12 mb-4">🏛️ Hiérarchie des rôles KWIKILA Care</h3>
          <div className="relative bg-[#FDFBF7] rounded-xl border border-[#E5EBE7] p-6">
            <div className="flex flex-col items-center gap-3">
              <RoleNode icon={Shield} label="👑 Admin KWIKILA (Super-Admin)" scope="Tous les hôpitaux" color="#1E4631"/>
              <div className="w-0.5 h-4 bg-[#E5EBE7]"/>
              <div className="grid grid-cols-3 gap-6 w-full">
                <RoleNode icon={Building2} label="🏥 Admin hôpital" scope="Son établissement" color="#D96C4A"/>
                <RoleNode icon={Landmark} label="🏛️ Ministère" scope="Stats agrégées (lecture)" color="#7A8471"/>
                <div/>
              </div>
              <div className="w-0.5 h-4 bg-[#E5EBE7]"/>
              <div className="grid grid-cols-3 gap-3 w-full">
                <RoleNode icon={Stethoscope} label="🩺 Médecin" scope="Actes médicaux" color="#2E5C7E" small/>
                <RoleNode icon={Syringe} label="💉 Infirmier(e)" scope="Soins & suivi" color="#2E7D32" small/>
                <RoleNode icon={ClipboardList} label="📋 Secrétaire" scope="Accueil & RDV" color="#E3A72F" small/>
              </div>
            </div>
          </div>
        </section>

        {/* ========== PAGE 3 — SUPER-ADMIN ========== */}
        <RolePage
          pageNum={3}
          chapter="Rôle 1/6"
          icon={Shield}
          emoji="👑"
          color={COLORS.green}
          name="Admin KWIKILA Care"
          roleKey="super_admin"
          subtitle="Équipe centrale · Kinshasa"
          desc="L'équipe centrale KWIKILA Care. Responsable de la plateforme, du support aux hôpitaux et du suivi commercial."
          allowed={[
            { icon: Building2, t: "Créer / activer / désactiver un hôpital" },
            { icon: Users, t: "Consulter la liste globale du personnel (tous hôpitaux)" },
            { icon: Building2, t: "Voir les statistiques détaillées d'un hôpital (Hospitalisés, Décès, RDV, Campagnes…)" },
            { icon: Landmark, t: "Créer les comptes Ministère (national ou provincial DPS)" },
            { icon: Megaphone, t: "Créer les campagnes nationales de santé publique (SMS + vidéos)" },
            { icon: Mail, t: "Recevoir et traiter les messages du formulaire de contact" },
            { icon: FileText, t: "Consulter le tableau financier + audit log complet" },
          ]}
          denied={[
            "Supprimer ou désactiver le Fondateur (protection absolue)",
            "Voir les mots de passe (bcrypt, jamais en clair)",
            "Modifier une fiche patient (rôle réservé à l'hôpital)",
            "Prescrire un traitement médical",
          ]}
          mockup={<MockSuperAdmin/>}
        />

        {/* ========== PAGE 4 — ADMIN HÔPITAL ========== */}
        <RolePage
          pageNum={4}
          chapter="Rôle 2/6"
          icon={Building2}
          emoji="🏥"
          color={COLORS.orange}
          name="Admin de l'hôpital"
          roleKey="hospital_admin"
          subtitle="Directeur médical · Médecin-chef"
          desc="Pilote son établissement sur la plateforme : équipe, patients, hospitalisations, décès et statistiques hôpital."
          allowed={[
            { icon: UserCog, t: "Créer et gérer les comptes de son équipe (médecin, infirmier, secrétaire)" },
            { icon: Users, t: "CRUD complet sur les patients de son hôpital" },
            { icon: BedDouble, t: "Hospitaliser / faire sortir un patient" },
            { icon: Cross, t: "Enregistrer un décès avec cause + notes" },
            { icon: Paperclip, t: "Uploader des pièces jointes (radios, ordonnances PDF)" },
            { icon: Calendar, t: "Créer, modifier, supprimer les RDV" },
            { icon: MessageSquare, t: "Déclencher les rappels SMS (J-15, J-3, 45min)" },
            { icon: FileText, t: "Consulter les registres Hospitalisation + Décès + Log SMS" },
          ]}
          denied={[
            "Voir les données d'un autre hôpital",
            "Créer une campagne nationale (réservé au Super-Admin)",
            "Créer un compte Super-Admin ou Ministère",
            "Ajouter une note médicale s'il n'est pas médecin",
          ]}
          mockup={<MockHospitalAdmin/>}
        />

        {/* ========== PAGE 5 — MÉDECIN ========== */}
        <RolePage
          pageNum={5}
          chapter="Rôle 3/6"
          icon={Stethoscope}
          emoji="🩺"
          color={COLORS.blue}
          name="Médecin"
          roleKey="doctor"
          subtitle="Praticien clinique"
          desc="Le SEUL rôle qui peut inscrire officiellement des notes médicales signées. Consulte, prescrit, hospitalise et décide."
          allowed={[
            { icon: FileText, t: "📝 EXCLUSIF : Ajouter des notes médicales signées à son nom" },
            { icon: Eye, t: "Consulter tout l'historique des notes (de tous les médecins)" },
            { icon: BedDouble, t: "Hospitaliser un patient (attribuer chambre + motif)" },
            { icon: Cross, t: "Enregistrer un décès (cause + notes)" },
            { icon: Paperclip, t: "Uploader radios / résultats / ordonnances" },
            { icon: MessageSquare, t: "Utiliser les conseils IA multilingues (5 langues)" },
            { icon: Calendar, t: "Créer et gérer ses RDV + rappels SMS" },
            { icon: Printer, t: "Imprimer la fiche patient A4 complète" },
          ]}
          denied={[
            "Modifier les notes d'un AUTRE médecin (traçabilité)",
            "Supprimer un patient (rôle admin uniquement)",
            "Créer un compte utilisateur",
            "Voir les patients d'un autre hôpital",
            "Créer une campagne nationale",
            "Accéder à la finance ou aux paramètres hôpital",
          ]}
          mockup={<MockDoctor/>}
        />

        {/* ========== PAGE 6 — INFIRMIÈRE ========== */}
        <RolePage
          pageNum={6}
          chapter="Rôle 4/6"
          icon={Syringe}
          emoji="💉"
          color="#2E7D32"
          name="Infirmier(e)"
          roleKey="nurse"
          subtitle="Personnel soignant"
          desc="Accompagne le patient au quotidien : accueil, constantes, préparation dossiers, éducation patient multilingue."
          allowed={[
            { icon: Eye, t: "Consulter la liste + fiches complètes des patients" },
            { icon: FileText, t: "Consulter les notes médicales des médecins" },
            { icon: BedDouble, t: "Voir les registres Hospitalisations et Décès" },
            { icon: Paperclip, t: "Uploader des pièces jointes (analyses, formulaires)" },
            { icon: Calendar, t: "Créer un RDV + déclencher les rappels SMS" },
            { icon: MessageSquare, t: "Utiliser les conseils IA multilingues pour éduquer le patient" },
            { icon: Globe, t: "Modifier la langue préférée du patient (5 langues)" },
          ]}
          denied={[
            "📝 Ajouter une note médicale (EXCLUSIF au médecin)",
            "🛏️ Hospitaliser un patient (admin ou médecin)",
            "☨ Enregistrer un décès (admin ou médecin)",
            "Supprimer un patient",
            "Créer un compte utilisateur",
            "Voir les patients d'un autre hôpital",
            "Accéder à la finance",
          ]}
          mockup={<MockNurse/>}
        />

        {/* ========== PAGE 7 — SECRÉTAIRE ========== */}
        <RolePage
          pageNum={7}
          chapter="Rôle 5/6"
          icon={ClipboardList}
          emoji="📋"
          color="#E3A72F"
          name="Secrétaire médical(e)"
          roleKey="secretary"
          subtitle="Accueil hospitalier · Front-office"
          desc="🌟 Rôle CLÉ : c'est souvent la 1ère personne à utiliser l'outil. Enregistre le patient, prend les RDV, envoie les rappels SMS."
          allowed={[
            { icon: Users, t: "📥 Créer un nouveau patient (identité, tél, adresse structurée, catégorie, langue)" },
            { icon: FileText, t: "Modifier les infos administratives d'un patient" },
            { icon: XCircle, t: "Supprimer un patient (si erreur d'enregistrement)" },
            { icon: Paperclip, t: "Uploader des pièces jointes (CNI, formulaires)" },
            { icon: Calendar, t: "Créer, modifier, supprimer les RDV" },
            { icon: MessageSquare, t: "Envoyer les rappels SMS (J-15, J-3, 45min)" },
            { icon: FileText, t: "Voir les registres Hospitalisations + Décès + Log SMS" },
            { icon: Printer, t: "Imprimer la fiche patient" },
          ]}
          denied={[
            "📝 Ajouter une note médicale (EXCLUSIF au médecin)",
            "🛏️ Hospitaliser / faire sortir un patient",
            "☨ Enregistrer un décès",
            "Créer un compte utilisateur",
            "Voir les patients d'un autre hôpital",
            "Créer une campagne",
            "Accéder à la finance",
          ]}
          mockup={<MockSecretary/>}
        />

        {/* ========== PAGE 8 — MINISTÈRE ========== */}
        <RolePage
          pageNum={8}
          chapter="Rôle 6/6"
          icon={Landmark}
          emoji="🏛️"
          color={COLORS.olive}
          name="Compte Ministère de la Santé"
          roleKey="ministry"
          subtitle="Autorité sanitaire · STRICTEMENT LECTURE SEULE"
          desc="Ministre national, DG SNIS, ou Chef DPS provincial. Consulte les statistiques agrégées, aucune donnée nominative exposée (secret médical)."
          allowed={[
            { icon: Building2, t: "Consulter nb hôpitaux partenaires actifs (national OU province)" },
            { icon: Users, t: "Consulter nb patients suivis, hospitalisations, décès" },
            { icon: FileText, t: "Consulter top 10 causes de décès + répartition par tranche d'âge" },
            { icon: Globe, t: "Consulter l'adoption linguistique (5 langues)" },
            { icon: MapPin, t: "Consulter la répartition géographique (villes + communes)" },
            { icon: Calendar, t: "Consulter l'évolution mensuelle sur 6 mois" },
            { icon: Printer, t: "Générer le RAPPORT OFFICIEL 5 pages A4 (PDF)" },
          ]}
          denied={[
            "🔒 Voir le nom, téléphone ou adresse d'un patient individuel",
            "Créer / modifier / supprimer un patient",
            "Créer / modifier / supprimer un hôpital",
            "Créer un utilisateur ou une campagne",
            "Voir les notes médicales ou les pièces jointes",
            "Envoyer des SMS",
            "Modifier son propre profil (géré par Super-Admin KWIKILA)",
          ]}
          extraNote="⚙️ Sécurité technique : tous les endpoints d'écriture refusent avec HTTP 403 pour ce rôle. Vérifié par 7 tests automatiques à chaque déploiement."
          mockup={<MockMinistry/>}
        />

        {/* ========== PAGE 9 — MATRICE RÉCAPITULATIVE ========== */}
        <section className="p-16 min-h-[297mm] page-break">
          <PageHeader chapter="Chapitre 3" title="Matrice récapitulative des permissions" pageNum={9}/>
          <p className="text-sm text-[#5C6B61] mt-4 mb-6 max-w-3xl">
            Cette matrice liste 15 actions clés et indique quel rôle a le droit de les effectuer.
            À afficher en salle de formation, à imprimer et à distribuer.
          </p>

          <MatrixTable/>

          <div className="mt-8 grid grid-cols-3 gap-3">
            <Legend color="#1E4631" label="✅ Autorisé" desc="L'utilisateur peut effectuer cette action sans restriction"/>
            <Legend color="#C62828" label="❌ Interdit" desc="Le système refuse l'action (HTTP 403 côté serveur)"/>
            <Legend color="#7A8471" label="👁️ Lecture seule" desc="Consultation autorisée mais aucune modification"/>
          </div>
        </section>

        {/* ========== PAGE 10 — SCÉNARIOS ========== */}
        <section className="p-16 min-h-[297mm] page-break">
          <PageHeader chapter="Chapitre 4" title="Scénarios pédagogiques" pageNum={10}/>
          <p className="text-sm text-[#5C6B61] mt-4 mb-8 max-w-3xl">
            4 scénarios réalistes à jouer en salle de formation pour montrer la collaboration entre les rôles.
          </p>

          <Scenario
            num="1"
            title="Arrivée d'un patient (grossesse)"
            color="#D96C4A"
            steps={[
              { role: "📋 Secrétaire", action: "Enregistre Mme KABEYA à l'accueil (tél, catégorie=Femme enceinte, langue=Lingala)" },
              { role: "📋 Secrétaire", action: "Planifie le RDV avec Dr MUKENDI le 15/03/2026 à 09h00" },
              { role: "⚙️ Système", action: "Envoie automatiquement le SMS de rappel J-15" },
              { role: "💉 Infirmière", action: "Accueille Mme KABEYA le jour J, uploade sa CNI" },
              { role: "🩺 Médecin", action: "Consulte + ajoute une note médicale signée + uploade l'ordonnance PDF" },
            ]}
          />

          <Scenario
            num="2"
            title="Hospitalisation d'un enfant"
            color="#2E7D32"
            steps={[
              { role: "🩺 Médecin", action: "Ouvre la fiche du petit KABASELE (3 ans, catégorie=Enfant 0-3)" },
              { role: "🩺 Médecin", action: "Clique 'Hospitaliser' → attribue chambre 12 + motif 'Paludisme sévère'" },
              { role: "💉 Infirmière", action: "Suit le patient hospitalisé, uploade les analyses" },
              { role: "🩺 Médecin", action: "Après guérison, clique 'Faire sortir' → patient repasse en externe" },
              { role: "🏛️ Ministère", action: "Voit l'hospitalisation puis la sortie dans ses stats agrégées (anonyme)" },
            ]}
          />

          <Scenario
            num="3"
            title="Campagne nationale COVID booster"
            color="#1E4631"
            steps={[
              { role: "👑 Super-Admin", action: "Ouvre /super-admin/campaigns → 'Nouvelle campagne'" },
              { role: "👑 Super-Admin", action: "Titre = 'Vaccination COVID booster', Cible = personnes âgées, Tous hôpitaux, Type = ad + URL YouTube" },
              { role: "⚙️ Système", action: "Envoie SMS + email à tous les patients ≥ 60 ans du réseau" },
              { role: "🏛️ Ministère", action: "Voit dans ses stats le nombre total de SMS envoyés" },
            ]}
          />

          <Scenario
            num="4"
            title="Rapport officiel mensuel Ministère"
            color="#7A8471"
            steps={[
              { role: "🏛️ DPS Kinshasa", action: "Se connecte à /ministry → voit ses stats provinciales (8 hôpitaux Kinshasa)" },
              { role: "🏛️ DPS Kinshasa", action: "Clique 'Rapport officiel' → génère le PDF 5 pages A4" },
              { role: "🏛️ DPS Kinshasa", action: "Signe manuellement → envoi papier au Ministère" },
              { role: "👑 Super-Admin", action: "Peut aussi générer le même rapport en vue nationale" },
            ]}
          />
        </section>

        {/* ========== PAGE 11 — CONTACT & SIGNATURE ========== */}
        <section className="p-16 min-h-[297mm]">
          <PageHeader chapter="Chapitre 5" title="Contact & prochaines étapes" pageNum={11}/>

          <div className="mt-12 grid grid-cols-2 gap-8">
            <div className="bg-[#1E4631] text-white rounded-2xl p-8">
              <HeartPulse className="w-10 h-10 text-[#D96C4A] mb-4"/>
              <h3 className="font-[Outfit] font-bold text-2xl mb-2">KWIKILA Care</h3>
              <p className="text-sm text-white/80 mb-6">La santé en confiance · Kinshasa, République Démocratique du Congo</p>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2"><Globe className="w-4 h-4"/> kwikilacare.com</div>
                <div className="flex items-center gap-2"><Mail className="w-4 h-4"/> contact@kwikilacare.com</div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4"/> +243 824 314 459</div>
                <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                  <span className="text-xs text-white/60">Fondateur : Paul MAKWIKILA PWADI</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-[Outfit] font-bold text-xl mb-4">Prochaines étapes après cette formation</h3>
              <ol className="space-y-4 text-sm">
                {[
                  ["Pratique guidée (1h)", "Chaque apprenant se connecte avec son rôle et refait les 4 scénarios"],
                  ["Comptes réels créés", "L'admin hôpital crée les vrais comptes des collègues via /dashboard/team"],
                  ["Import patients existants (optionnel)", "Migration de la base papier vers KWIKILA en 1 clic (CSV)"],
                  ["Support continu", "Contactez-nous à contact@kwikilacare.com pour toute question"],
                ].map(([t, d], i) => (
                  <li key={i} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#D96C4A] text-white font-bold grid place-items-center shrink-0 text-xs">{i+1}</div>
                    <div>
                      <div className="font-semibold text-[#1E4631]">{t}</div>
                      <div className="text-[#5C6B61] text-xs mt-0.5">{d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Signature du formateur */}
          <div className="mt-16 pt-8 border-t-2 border-[#1E4631]">
            <div className="grid grid-cols-2 gap-12">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#5C6B61] mb-2">Formateur / Formatrice</div>
                <div className="h-20 border-b border-[#5C6B61]"></div>
                <div className="text-xs mt-2 text-[#5C6B61]">Nom, date & signature</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#5C6B61] mb-2">Représentant hôpital</div>
                <div className="h-20 border-b border-[#5C6B61]"></div>
                <div className="text-xs mt-2 text-[#5C6B61]">Nom, fonction, date & signature</div>
              </div>
            </div>
          </div>

          <div className="text-[10px] text-[#8A9990] text-center mt-12 pt-6 border-t border-[#E5EBE7]">
            Guide de formation KWIKILA Care · Version 3.5 (Février 2026) ·
            Ce document est susceptible d'évoluer avec les nouvelles versions de la plateforme.
          </div>
        </section>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: #ffffff !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .page-break { page-break-after: always; }
          section { min-height: 297mm; }
        }
      `}</style>
    </div>
  );
}

/* ========== COMPOSANTS UTILITAIRES ========== */

function PageHeader({ chapter, title, pageNum }) {
  return (
    <div className="flex items-end justify-between pb-4 border-b-2 border-[#1E4631]">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#D96C4A] font-semibold mb-1">{chapter}</div>
        <h1 className="font-[Outfit] font-bold text-3xl">{title}</h1>
      </div>
      <div className="text-right text-xs text-[#5C6B61]">
        <div className="font-semibold text-[#1E4631]">KWIKILA Care</div>
        <div>Guide de formation · Page {pageNum}/11</div>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, num, label }) {
  return (
    <div className="bg-white border border-[#E5EBE7] rounded-xl p-3 text-center">
      <Icon className="w-5 h-5 mx-auto text-[#1E4631]"/>
      <div className="font-[Outfit] font-bold text-2xl mt-1">{num}</div>
      <div className="text-[10px] text-[#5C6B61] uppercase tracking-wider">{label}</div>
    </div>
  );
}

function RoleNode({ icon: Icon, label, scope, color, small }) {
  return (
    <div className={`bg-white rounded-xl border-2 ${small ? "p-2" : "p-3"} text-center shadow-sm`} style={{ borderColor: color }}>
      <Icon className={`${small ? "w-5 h-5" : "w-6 h-6"} mx-auto`} style={{ color }}/>
      <div className={`${small ? "text-xs" : "text-sm"} font-semibold mt-1`}>{label}</div>
      <div className="text-[9px] text-[#5C6B61] mt-0.5">{scope}</div>
    </div>
  );
}

function RolePage({ pageNum, chapter, icon: Icon, emoji, color, name, subtitle, desc, allowed, denied, mockup, extraNote, roleKey }) {
  return (
    <section className="p-16 min-h-[297mm] page-break">
      <PageHeader chapter={chapter} title={name} pageNum={pageNum}/>

      {/* Role hero */}
      <div className="mt-6 flex items-start gap-5 p-6 rounded-2xl" style={{ background: `${color}0D` }}>
        <div className="w-16 h-16 rounded-2xl grid place-items-center text-3xl shrink-0" style={{ background: color, color: "white" }}>
          {emoji}
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest font-semibold" style={{ color }}>{subtitle}</div>
          <h2 className="font-[Outfit] font-bold text-2xl mt-0.5">{name}</h2>
          <p className="text-sm text-[#3F4A44] mt-2 leading-relaxed">{desc}</p>
        </div>
        {roleKey && (
          <a href={`/formation/quiz/${roleKey}`} target="_blank" rel="noopener" className="print:hidden shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition" style={{ background: color }}>
            🎓 Passer le quiz
          </a>
        )}
      </div>

      {/* Grid : autorisés / interdits / mockup */}
      <div className="mt-8 grid grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-[#2E7D32]"/>
            <h3 className="font-[Outfit] font-bold text-sm uppercase tracking-wider text-[#2E7D32]">Ce que je peux faire</h3>
          </div>
          <ul className="space-y-2.5 text-sm">
            {allowed.map((a, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <a.icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }}/>
                <span className="text-[#3F4A44] leading-snug">{a.t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <XCircle className="w-5 h-5 text-[#C62828]"/>
            <h3 className="font-[Outfit] font-bold text-sm uppercase tracking-wider text-[#C62828]">Interdictions absolues</h3>
          </div>
          <ul className="space-y-2.5 text-sm">
            {denied.map((d, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <Lock className="w-4 h-4 mt-0.5 shrink-0 text-[#C62828]"/>
                <span className="text-[#3F4A44] leading-snug">{d}</span>
              </li>
            ))}
          </ul>

          {extraNote && (
            <div className="mt-4 p-3 bg-[#C62828]/5 border-l-4 border-[#C62828] rounded-r text-xs text-[#5C6B61] italic">
              {extraNote}
            </div>
          )}
        </div>
      </div>

      {/* Mockup */}
      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-[#5C6B61] mb-2">Aperçu écran</div>
        {mockup}
      </div>
    </section>
  );
}

/* ========== MOCKUPS UI ========== */

function MockShell({ children, sidebar }) {
  return (
    <div className="rounded-xl border border-[#E5EBE7] overflow-hidden bg-[#FDFBF7]">
      <div className="flex">
        {sidebar}
        <div className="flex-1 p-4 bg-white">
          {children}
        </div>
      </div>
    </div>
  );
}

function MockSidebar({ items, active }) {
  return (
    <div className="w-40 bg-[#1E4631] text-white p-3 min-h-[220px]">
      <div className="flex items-center gap-1.5 mb-3 pb-2 border-b border-white/10">
        <div className="w-5 h-5 rounded bg-[#D96C4A] grid place-items-center text-[10px] font-bold">K</div>
        <div className="text-xs font-semibold">KWIKILA</div>
      </div>
      <ul className="space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className={`text-[10px] px-2 py-1 rounded flex items-center gap-1.5 ${it === active ? "bg-[#D96C4A] font-semibold" : "text-white/70"}`}>
            <ChevronRight className="w-2 h-2"/> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MockSuperAdmin() {
  return (
    <MockShell sidebar={<MockSidebar items={["Vue globale", "Hôpitaux", "Équipe", "Finances", "Ministère", "Campagnes", "Messages"]} active="Hôpitaux"/>}>
      <div className="text-[10px] uppercase text-[#5C6B61] tracking-widest">Super-Admin</div>
      <div className="font-[Outfit] font-bold text-sm mb-3">Hôpitaux partenaires (26)</div>
      <div className="space-y-1.5">
        {[
          ["Hôpital Saint-Joseph Kinshasa", "active", "Kinshasa · +243..."],
          ["Clinique Ngaliema", "active", "Kinshasa · +243..."],
          ["Hôpital Lubumbashi", "pending", "Haut-Katanga · +243..."],
        ].map(([n, s, addr], i) => (
          <div key={i} className="p-2 rounded border border-[#E5EBE7] bg-white flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold">{n}</div>
              <div className="text-[9px] text-[#5C6B61]">{addr}</div>
            </div>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${s === "active" ? "bg-[#2E7D32]/10 text-[#2E7D32]" : "bg-[#E3A72F]/10 text-[#E3A72F]"}`}>{s}</span>
          </div>
        ))}
      </div>
    </MockShell>
  );
}

function MockHospitalAdmin() {
  return (
    <MockShell sidebar={<MockSidebar items={["Accueil", "Patients", "Hospitalisations", "Décès", "RDV", "Équipe", "SMS Log"]} active="Accueil"/>}>
      <div className="text-[10px] uppercase text-[#5C6B61] tracking-widest">Admin Hôpital · Saint-Joseph</div>
      <div className="font-[Outfit] font-bold text-sm mb-3">Vue d'ensemble</div>
      <div className="grid grid-cols-4 gap-2">
        {[
          ["Patients", "247", "#1E4631"],
          ["Hospitalisés", "12", "#D96C4A"],
          ["Décès mois", "3", "#C62828"],
          ["RDV semaine", "38", "#2E5C7E"],
        ].map(([l, n, c], i) => (
          <div key={i} className="p-2 rounded border border-[#E5EBE7] bg-white">
            <div className="text-[9px] text-[#5C6B61] uppercase">{l}</div>
            <div className="font-[Outfit] font-bold text-lg" style={{ color: c }}>{n}</div>
          </div>
        ))}
      </div>
    </MockShell>
  );
}

function MockDoctor() {
  return (
    <MockShell sidebar={<MockSidebar items={["Patients", "Notes", "Hospitalisations", "RDV", "Conseils IA"]} active="Patients"/>}>
      <div className="text-[10px] uppercase text-[#5C6B61] tracking-widest">Dr. MUKENDI · Médecin</div>
      <div className="font-[Outfit] font-bold text-sm mb-3">Fiche patient : Mme KABEYA</div>
      <div className="p-2 rounded bg-[#2E5C7E]/5 border border-[#2E5C7E]/20 text-xs mb-2">
        <div className="font-semibold text-[#2E5C7E]">📝 Nouvelle note médicale</div>
        <div className="text-[10px] mt-1 text-[#3F4A44]">Consultation prénatale 7ème mois — TA 12/8, RCF 145 bpm. Vitamines B9 + fer prescrites. Prochain RDV J+14.</div>
        <div className="text-[9px] text-[#5C6B61] mt-1">Signé Dr. MUKENDI · 15/03/2026 14:30</div>
      </div>
      <div className="flex gap-1 text-[9px]">
        <span className="px-1.5 py-0.5 rounded bg-[#D96C4A]/15 text-[#D96C4A]">🛏️ Hospitaliser</span>
        <span className="px-1.5 py-0.5 rounded bg-[#1E4631]/10 text-[#1E4631]">📎 Ordonnance</span>
        <span className="px-1.5 py-0.5 rounded bg-[#2E7D32]/10 text-[#2E7D32]">🖨️ Imprimer</span>
      </div>
    </MockShell>
  );
}

function MockNurse() {
  return (
    <MockShell sidebar={<MockSidebar items={["Patients", "RDV", "Conseils IA", "Pièces jointes"]} active="Conseils IA"/>}>
      <div className="text-[10px] uppercase text-[#5C6B61] tracking-widest">Inf. LUKUSA · Personnel soignant</div>
      <div className="font-[Outfit] font-bold text-sm mb-3">🌐 Conseil IA multilingue</div>
      <div className="p-2 rounded bg-[#2E7D32]/5 border border-[#2E7D32]/20 text-[10px]">
        <div className="flex gap-1 mb-1">
          {["FR", "LN", "SW", "TS", "KG"].map(l => (
            <span key={l} className={`px-1.5 py-0.5 rounded text-[8px] ${l === "LN" ? "bg-[#2E7D32] text-white" : "bg-[#E5EBE7]"}`}>{l}</span>
          ))}
        </div>
        <div className="italic text-[#3F4A44]">
          "Bomama, kolia bilei ya kitoko na sanduku ya butu — bibuma, ndunda, mai... Loba na monganga soki mabe ezali."
        </div>
        <div className="text-[9px] text-[#5C6B61] mt-1">Conseil nutrition prénatal · Lingala · Claude Sonnet 4.5</div>
      </div>
    </MockShell>
  );
}

function MockSecretary() {
  return (
    <MockShell sidebar={<MockSidebar items={["Nouveau patient", "Patients", "RDV", "SMS Log"]} active="Nouveau patient"/>}>
      <div className="text-[10px] uppercase text-[#5C6B61] tracking-widest">Secrétaire · Accueil</div>
      <div className="font-[Outfit] font-bold text-sm mb-3">📥 Enregistrer un nouveau patient</div>
      <div className="space-y-1.5 text-[10px]">
        {[
          ["Nom complet", "Mme KABEYA Marie"],
          ["Téléphone", "+243 999 456 789"],
          ["Catégorie", "🤰 Femme enceinte"],
          ["Langue préférée", "🇨🇩 Lingala"],
          ["Commune / Quartier", "Ngaliema · Q.7"],
        ].map(([l, v], i) => (
          <div key={i} className="flex justify-between border-b border-dashed border-[#E5EBE7] pb-1">
            <span className="text-[#5C6B61]">{l}</span>
            <span className="font-medium">{v}</span>
          </div>
        ))}
      </div>
      <button className="mt-3 px-2 py-1 rounded bg-[#E3A72F] text-white text-[9px] font-semibold">✓ Enregistrer + programmer RDV</button>
    </MockShell>
  );
}

function MockMinistry() {
  return (
    <MockShell sidebar={<MockSidebar items={["Vue nationale", "Rapport officiel", "Actualiser"]} active="Vue nationale"/>}>
      <div className="text-[10px] uppercase text-[#5C6B61] tracking-widest">🏛️ Ministère · Lecture seule</div>
      <div className="font-[Outfit] font-bold text-sm mb-3">Statistiques nationales RDC</div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        {[
          ["Hôpitaux", "26"],
          ["Patients", "5 847"],
          ["Hospitalisés", "127"],
          ["Décès", "34"],
        ].map(([l, n], i) => (
          <div key={i} className="p-1.5 rounded border border-[#E5EBE7] bg-white">
            <div className="text-[8px] text-[#5C6B61] uppercase">{l}</div>
            <div className="font-[Outfit] font-bold text-sm text-[#7A8471]">{n}</div>
          </div>
        ))}
      </div>
      <div className="p-1.5 rounded bg-[#7A8471]/10 text-[9px] text-[#5C6B61] italic">
        🔒 Aucune donnée nominative — secret médical respecté
      </div>
    </MockShell>
  );
}

/* ========== MATRICE ========== */

function MatrixTable() {
  const rows = [
    ["Voir tous les hôpitaux", ["✅", "❌", "❌", "❌", "❌", "👁️"]],
    ["Créer/désactiver hôpital", ["✅", "❌", "❌", "❌", "❌", "❌"]],
    ["Créer utilisateur", ["✅ toutes", "✅ son équipe", "❌", "❌", "❌", "❌"]],
    ["Créer un patient", ["❌", "✅", "✅", "✅", "✅", "❌"]],
    ["Supprimer patient", ["❌", "✅", "❌", "❌", "✅", "❌"]],
    ["Ajouter note médicale", ["❌", "❌", "✅ EXCLUSIF", "❌", "❌", "❌"]],
    ["Hospitaliser/sortir", ["❌", "✅", "✅", "❌", "❌", "❌"]],
    ["Enregistrer décès", ["❌", "✅", "✅", "❌", "❌", "❌"]],
    ["Uploader pièce jointe", ["❌", "✅", "✅", "✅", "✅", "❌"]],
    ["Créer/gérer RDV", ["❌", "✅", "✅", "✅", "✅", "❌"]],
    ["Envoyer rappel SMS", ["❌", "✅", "✅", "✅", "✅", "❌"]],
    ["Créer campagne nationale", ["✅", "❌", "❌", "❌", "❌", "❌"]],
    ["Voir stats hôpital", ["✅ tous", "✅ sien", "👁️", "👁️", "👁️", "👁️ agr."]],
    ["Voir finance/billing", ["✅", "❌", "❌", "❌", "❌", "❌"]],
    ["Générer rapport ministère", ["✅", "❌", "❌", "❌", "❌", "✅"]],
  ];
  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr className="bg-[#1E4631] text-white">
          <th className="text-left px-2 py-2 font-semibold" style={{ width: "34%" }}>Action</th>
          <th className="px-1.5 py-2 text-center">👑</th>
          <th className="px-1.5 py-2 text-center">🏥</th>
          <th className="px-1.5 py-2 text-center">🩺</th>
          <th className="px-1.5 py-2 text-center">💉</th>
          <th className="px-1.5 py-2 text-center">📋</th>
          <th className="px-1.5 py-2 text-center">🏛️</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FDFBF7]"}>
            <td className="px-2 py-1.5 border-b border-[#E5EBE7]">{r[0]}</td>
            {r[1].map((v, j) => (
              <td key={j} className="px-1.5 py-1.5 text-center border-b border-[#E5EBE7]" style={{ fontSize: "10px" }}>{v}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Legend({ color, label, desc }) {
  return (
    <div className="p-3 rounded-lg border border-[#E5EBE7] bg-[#FDFBF7]">
      <div className="font-semibold text-sm" style={{ color }}>{label}</div>
      <div className="text-[10px] text-[#5C6B61] mt-1">{desc}</div>
    </div>
  );
}

function Scenario({ num, title, color, steps }) {
  return (
    <div className="mb-6 rounded-2xl border border-[#E5EBE7] overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-3" style={{ background: `${color}15`, borderBottom: `1px solid ${color}30` }}>
        <div className="w-8 h-8 rounded-full text-white font-bold grid place-items-center" style={{ background: color }}>{num}</div>
        <div className="font-[Outfit] font-bold text-sm" style={{ color }}>{title}</div>
      </div>
      <div className="p-4 space-y-2">
        {steps.map((s, i) => (
          <div key={i} className="flex gap-3 items-start">
            <div className="text-[10px] font-mono text-[#5C6B61] w-8 shrink-0 pt-0.5">#{i + 1}</div>
            <div className="text-[11px] font-semibold w-32 shrink-0" style={{ color }}>{s.role}</div>
            <div className="text-xs text-[#3F4A44]">{s.action}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
